/* eslint-disable react/jsx-no-useless-fragment */
import type { ThemeKey, ThemeVariantStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { Fragment } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ICONS } from 'Components/utils/icon/icons';
import { Badge } from 'Components/frameworks/badge/Badge';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { themes } from './InputButton.themes';
export type InputButtonProps = {
  badgeCount?: number;
  children?: ReactNode;
  className?: string;
  dataTestId?: string;
  hasSwitch?: boolean;
  iconName?: IconName;
  isActive?: boolean;
  isDisabled?: boolean;
  isFilled?: boolean;
  isLoading?: boolean;
  onClear?: (arg0: React.SyntheticEvent) => void;
  onClick?: (arg0: React.SyntheticEvent) => void;
  theme?: ThemeKey<typeof themes>;
  title?: string;
};
export const InputButton = ({
  children,
  className,
  dataTestId,
  badgeCount,
  hasSwitch = false,
  iconName,
  isActive,
  isDisabled,
  isFilled,
  isLoading,
  onClear,
  onClick,
  theme = 'default',
  title,
}: InputButtonProps) => {
  const {
    thBadge,
    thButton,
    thChildren,
    thClear,
    thClearSize,
    thIcon,
    thIconRight,
    thIconCaret,
    thIconSize,
    thSpinner,
    thRoot,
    thSwitch,
    thSwitchButton,
    thSwitchToggler,
  } = themes({
    isActive,
    isDisabled,
    isFilled,
    isLoading,
  })[theme] as ThemeVariantStyles<typeof themes>;

  if (!iconName && !children) {
    return null;
  }

  const hasClear = onClear && isFilled && !isLoading && !isDisabled;
  return (
    <div className={classnames(thRoot, className)}>
      <button
        className={thButton}
        data-testid={dataTestId}
        disabled={isDisabled}
        onClick={onClick}
        title={title}
        type="button"
      >
        {!!iconName && <Icon className={classnames(thIcon)} name={iconName} width={thIconSize} />}
        <span className={thChildren}>{children}</span>
        {!!badgeCount && (
          <Badge
            className={thBadge}
            label={<span className="-me-1 mt-1">{badgeCount}</span>}
            variant="pill"
          />
        )}
      </button>

      {!hasSwitch && (
        <Fragment>
          {hasClear ? (
            <button
              data-testid="InputButtonClear"
              className={thClear}
              disabled={isDisabled}
              onClick={onClear}
              type="button"
            >
              <Icon name={ICONS.CROSS} width={thClearSize} />
            </button>
          ) : (
            <button className={thIconRight} onClick={onClick} type="button">
              {isLoading ? (
                <Spinner className={thSpinner} size="small" />
              ) : (
                <Icon className={thIconCaret} name={ICONS.ARROW_DOWN} width="0.625rem" />
              )}
            </button>
          )}
        </Fragment>
      )}

      {hasSwitch && (
        <button
          className={thSwitchButton}
          data-testid="InputButtonClear"
          onClick={hasClear ? onClear : onClick}
          type="button"
        >
          <span className={thSwitch}>
            <span aria-hidden className={thSwitchToggler}>
              <Icon name={isFilled ? ICONS.ON : ICONS.OFF} width="0.5rem" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
};
